import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Button, Drawer, Modal, Tooltip, message } from "antd";
import {
  CheckOutlined,
  DeleteOutlined,
  CalculatorOutlined,
  HistoryOutlined
} from "@ant-design/icons";
import ReportButton from "@controls/report-button";
import { REPORT_VID } from "@components/service/reports/report-types";
import {
  deleteDocExp,
  docExpCheckInMany,
  docExpCheckOutMany,
  docExpConfirm,
  docExpRecalculate,
  fetchDocExpRecalculateCheck
} from "./doc-exp-api";
import DocExpCard from "./doc-exp-card";
import DocExpLifecycleModal from "./doc-exp-lifecycle-modal";
import UserMonitorDrawer from "@components/service/monitor/user-monitor-drawer";
import { STATUS, getLawFlag } from "./doc-exp-utils";
import arrowDownSrc from "./assets/arrow-down.svg";
import arrowDownMutedSrc from "./assets/arrow-down-muted.svg";
import arrowUpSrc from "./assets/arrow-up.svg";
import arrowUpMutedSrc from "./assets/arrow-up-muted.svg";
import xSrc from "./assets/x.svg";
import "./doc-exp.css";

const DRAWER_WIDTH_KEY = "planOnline.docExp.cardDrawer.width";
const MIN_WIDTH = 560;
const MAX_WIDTH = 960;
const DEFAULT_WIDTH = 720;
const SCREEN_MARGIN = 80;

const maxAllowedWidth = () => {
  if (typeof window === "undefined") return MAX_WIDTH;
  return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, window.innerWidth - SCREEN_MARGIN));
};

const clampWidth = (value) => {
  const max = maxAllowedWidth();
  const n = Number(value);
  if (!Number.isFinite(n)) return Math.min(DEFAULT_WIDTH, max);
  return Math.min(max, Math.max(MIN_WIDTH, Math.round(n)));
};

const loadWidth = () => {
  try {
    return clampWidth(localStorage.getItem(DRAWER_WIDTH_KEY) ?? DEFAULT_WIDTH);
  } catch {
    return DEFAULT_WIDTH;
  }
};

const saveWidth = (value) => {
  try {
    localStorage.setItem(DRAWER_WIDTH_KEY, String(clampWidth(value)));
  } catch {
    /* ignore */
  }
};

/**
 * Drawer карточки путёвки.
 */
const DocExp = ({
  open,
  idDoc,
  isNew = false,
  canEdit = true,
  reloadToken = 0,
  onClose,
  onSaved
}) => {
  const law = useSelector((state) => state.user.session?.law);
  const canDeleteLaw = getLawFlag(law, 31) === 1;
  const canViewMonitor = getLawFlag(law, 67) === 1;
  const [size, setSize] = useState(loadWidth);
  const [saving, setSaving] = useState(false);
  const [canSave, setCanSave] = useState(false);
  const [titleNum, setTitleNum] = useState(null);
  const [cardMeta, setCardMeta] = useState(null);
  const [activeTab, setActiveTab] = useState("header");
  const [lifecycle, setLifecycle] = useState(null);
  const [localReloadToken, setLocalReloadToken] = useState(0);
  const [recalcAllowed, setRecalcAllowed] = useState(true);
  const [recalcBlockReason, setRecalcBlockReason] = useState("");
  const [monitorOpen, setMonitorOpen] = useState(false);
  const submitRef = useRef(() => {});

  useEffect(() => {
    if (!open) {
      setSaving(false);
      setCanSave(false);
      setTitleNum(null);
      setCardMeta(null);
      setActiveTab("header");
      setLifecycle(null);
      setRecalcAllowed(true);
      setRecalcBlockReason("");
      setMonitorOpen(false);
      return;
    }
    setTitleNum(isNew ? null : idDoc);
    if (!isNew && idDoc != null) {
      // Пока проверка не пришла — кнопку не даём нажать.
      setRecalcAllowed(false);
      setRecalcBlockReason("");
    }
  }, [open, idDoc, isNew]);

  useEffect(() => {
    if (!open || isNew || idDoc == null) {
      if (isNew || idDoc == null) {
        setRecalcAllowed(true);
        setRecalcBlockReason("");
      }
      return undefined;
    }
    let cancelled = false;
    setRecalcAllowed(false);
    fetchDocExpRecalculateCheck(idDoc)
      .then((data) => {
        if (cancelled) return;
        const allowed = Boolean(data?.allowed ?? data?.Allowed);
        setRecalcAllowed(allowed);
        setRecalcBlockReason(
          allowed ? "" : data?.reason || data?.Reason || "Нельзя пересчитать размещение"
        );
      })
      .catch(() => {
        if (cancelled) return;
        setRecalcAllowed(false);
        setRecalcBlockReason("Не удалось проверить возможность пересчёта");
      });
    return () => {
      cancelled = true;
    };
  }, [open, isNew, idDoc, reloadToken, localReloadToken]);

  useEffect(() => {
    if (!open) return undefined;
    const onWinResize = () => setSize((prev) => clampWidth(prev));
    onWinResize();
    window.addEventListener("resize", onWinResize);
    return () => window.removeEventListener("resize", onWinResize);
  }, [open]);

  const onResize = useCallback((next) => {
    setSize(clampWidth(next));
  }, []);

  const onResizeEnd = useCallback(
    (finalSize) => {
      const next = clampWidth(typeof finalSize === "number" ? finalSize : size);
      setSize(next);
      saveWidth(next);
    },
    [size]
  );

  const onCardState = useCallback((state) => {
    submitRef.current = state.submit;
    setSaving(state.saving);
    setCanSave(state.canSave);
    setTitleNum(state.titleNum);
    if (state.activeTab) setActiveTab(state.activeTab);
    if (state.cardMeta !== undefined) setCardMeta(state.cardMeta);
  }, []);

  const canCheckIn =
    canEdit &&
    !isNew &&
    cardMeta &&
    !cardMeta.isClosed &&
    (cardMeta.idStatus === STATUS.NEW || cardMeta.idStatus === STATUS.CONFIRMED);

  const canCheckOut =
    canEdit &&
    !isNew &&
    cardMeta &&
    !cardMeta.isClosed &&
    cardMeta.idStatus === STATUS.CHECKED_IN;

  const canConfirm =
    canEdit &&
    !isNew &&
    cardMeta &&
    !cardMeta.isClosed &&
    cardMeta.idStatus === STATUS.NEW;

  const canDelete =
    canDeleteLaw &&
    !isNew &&
    cardMeta &&
    !cardMeta.isClosed &&
    cardMeta.idStatus != null &&
    cardMeta.idStatus < STATUS.CHECKED_IN &&
    !cardMeta.dateBeg;

  const canRecalculate =
    canEdit &&
    !isNew &&
    cardMeta &&
    !cardMeta.isClosed &&
    cardMeta.idStatus !== STATUS.CANCELLED &&
    recalcAllowed;

  const recalculateTitle = !recalcAllowed && recalcBlockReason ? recalcBlockReason : "Пересчёт";

  const onConfirm = useCallback(() => {
    if (!canConfirm || !cardMeta?.idDoc) return;
    Modal.confirm({
      title: "Подтвердить путёвку?",
      content: `Путёвка #${cardMeta.idDoc}`,
      okText: "Да",
      cancelText: "Отмена",
      okButtonProps: { type: "primary" },
      onOk: () =>
        docExpConfirm(cardMeta.idDoc)
          .then(() => {
            message.success("Подтверждено");
            setLocalReloadToken((t) => t + 1);
            onSaved?.();
          })
          .catch((e) => {
            message.error(e.message || "Не удалось подтвердить");
          })
    });
  }, [canConfirm, cardMeta, onSaved]);

  const onDelete = useCallback(() => {
    if (!canDelete || !cardMeta?.idDoc) return;
    Modal.confirm({
      title: "Удалить путёвку?",
      content: `#${cardMeta.idDoc}`,
      okText: "Удалить",
      cancelText: "Отмена",
      okButtonProps: { danger: true, type: "primary" },
      onOk: () =>
        deleteDocExp(cardMeta.idDoc)
          .then(() => {
            message.success("Удалено");
            onSaved?.();
            onClose?.();
          })
          .catch((e) => {
            message.error(e.message || "Не удалось удалить");
          })
    });
  }, [canDelete, cardMeta, onSaved, onClose]);

  const onRecalculate = useCallback(() => {
    if (!canRecalculate || !cardMeta?.idDoc) return;
    Modal.confirm({
      title: "Пересчитать размещение?",
      content: `Путёвка #${cardMeta.idDoc}`,
      okText: "Пересчитать",
      cancelText: "Отмена",
      okButtonProps: { type: "primary" },
      onOk: () =>
        docExpRecalculate(cardMeta.idDoc)
          .then(() => {
            message.success("Пересчитано");
            setLocalReloadToken((t) => t + 1);
            onSaved?.();
          })
          .catch((e) => {
            message.error(e.message || "Не удалось пересчитать");
            setLocalReloadToken((t) => t + 1);
          })
    });
  }, [canRecalculate, cardMeta, onSaved]);

  const onLifecycleOk = useCallback(
    async (dateFact) => {
      if (!lifecycle?.row?.idDoc) return;
      const isIn = lifecycle.mode === "check-in";
      try {
        const data = isIn
          ? await docExpCheckInMany([lifecycle.row.idDoc], dateFact)
          : await docExpCheckOutMany([lifecycle.row.idDoc], dateFact);
        setLifecycle(null);
        const ok = Number(data?.ok ?? data?.Ok) || 0;
        const errors = data?.errors || data?.Errors || [];
        if (ok && !(Array.isArray(errors) && errors.length)) {
          message.success(isIn ? "Заселено" : "Выселено");
        } else if (ok) {
          message.warning(isIn ? "Заселено с замечаниями" : "Выселено с замечаниями");
        } else {
          const msg =
            (Array.isArray(errors) && (errors[0]?.message || errors[0]?.Message)) ||
            "Не выполнено";
          message.error(msg);
          return;
        }
        setLocalReloadToken((t) => t + 1);
        onSaved?.();
      } catch (e) {
        message.error(e.message || "Ошибка операции");
      }
    },
    [lifecycle, onSaved]
  );

  const roomTitle = [cardMeta?.numberName, cardMeta?.catName].filter(Boolean).join(" / ");
  const title = isNew
    ? "Новая путёвка"
    : `Путёвка${idDoc != null ? ` #${idDoc}` : titleNum != null ? ` #${titleNum}` : ""}${roomTitle ? ` · ${roomTitle}` : ""}`;

  return (
    <>
      <Drawer
        placement="right"
        title={title}
        size={size}
        maxSize={maxAllowedWidth()}
        open={open}
        onClose={onClose}
        destroyOnClose
        maskClosable={!saving}
        keyboard={!saving}
        rootClassName="doc-exp-drawer"
        closable={false}
        extra={
          <div className="doc-exp-drawer__extra">
            {!isNew && idDoc != null ? (
              <>
                <Tooltip title="Заселить">
                  <button
                    type="button"
                    className="doc-exps__checkin-icon-btn"
                    disabled={!canCheckIn || saving}
                    aria-label="Заселить"
                    onClick={() =>
                      cardMeta && setLifecycle({ mode: "check-in", row: cardMeta })
                    }
                  >
                    <span className="doc-exps__act-icon">
                      <img src={canCheckIn ? arrowDownSrc : arrowDownMutedSrc} alt="" />
                    </span>
                  </button>
                </Tooltip>
                <Tooltip title="Выселить">
                  <button
                    type="button"
                    className="doc-exps__checkout-icon-btn"
                    disabled={!canCheckOut || saving}
                    aria-label="Выселить"
                    onClick={() =>
                      cardMeta && setLifecycle({ mode: "check-out", row: cardMeta })
                    }
                  >
                    <span className="doc-exps__act-icon">
                      <img src={canCheckOut ? arrowUpSrc : arrowUpMutedSrc} alt="" />
                    </span>
                  </button>
                </Tooltip>
                <Tooltip title="Подтвердить">
                  <Button
                    icon={<CheckOutlined />}
                    disabled={!canConfirm || saving}
                    aria-label="Подтвердить"
                    onClick={onConfirm}
                  />
                </Tooltip>
                <Tooltip title={recalculateTitle}>
                  <Button
                    icon={<CalculatorOutlined />}
                    disabled={!canRecalculate || saving}
                    aria-label="Пересчёт"
                    onClick={onRecalculate}
                  />
                </Tooltip>
                <Tooltip title="Удалить">
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    disabled={!canDelete || saving}
                    aria-label="Удалить"
                    onClick={onDelete}
                  />
                </Tooltip>
              </>
            ) : null}
            <ReportButton
              vid={REPORT_VID.TRAVEL}
              id={idDoc}
              compact
              disabled={isNew || idDoc == null}
            />
            <Tooltip title="Действия пользователей">
              <Button
                icon={<HistoryOutlined />}
                aria-label="Действия пользователей"
                disabled={isNew || idDoc == null || !canViewMonitor || saving}
                onClick={() => setMonitorOpen(true)}
              />
            </Tooltip>
            <button
              type="button"
              className="doc-exp-drawer__close-btn"
              aria-label="Закрыть"
              title="Закрыть"
              disabled={saving}
              onClick={onClose}
            >
              <span className="doc-exp-drawer__close">
                <img src={xSrc} alt="" />
              </span>
            </button>
          </div>
        }
        styles={{
          body: { display: "flex", flexDirection: "column", padding: "10px 12px", overflow: "hidden" }
        }}
        resizable={{
          onResize,
          onResizeEnd
        }}
        footer={
          activeTab === "header" ? (
            <div className="doc-exp-drawer__footer">
              <Button
                type="primary"
                className="doc-exp-drawer__save"
                loading={saving}
                disabled={!canSave}
                onClick={() => submitRef.current?.()}
              >
                ОК
              </Button>
              <Button className="doc-exp-drawer__cancel" onClick={onClose} disabled={saving}>
                Отмена
              </Button>
            </div>
          ) : null
        }
      >
        {open && (idDoc != null || isNew) ? (
          <DocExpCard
            idDoc={idDoc}
            isNew={isNew}
            open={open}
            canEdit={canEdit}
            reloadToken={reloadToken + localReloadToken}
            onSaved={onSaved}
            onClose={onClose}
            onStateChange={onCardState}
          />
        ) : null}
      </Drawer>

      <DocExpLifecycleModal
        open={Boolean(lifecycle)}
        mode={lifecycle?.mode}
        row={lifecycle?.row}
        onCancel={() => setLifecycle(null)}
        onOk={onLifecycleOk}
      />

      <UserMonitorDrawer
        open={monitorOpen}
        onClose={() => setMonitorOpen(false)}
        title={`Путёвка #${idDoc} — действия пользователей`}
        fixedFilter={{ idObjZag: idDoc, vidFrom: 5000, vidTo: 5999 }}
        infoText="Все действия по путёвке"
      />
    </>
  );
};

export default DocExp;
